import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

// Batch delay in ms - accumulate updates before processing
const BATCH_DELAY_MS = 1000;

/**
 * Custom hook for real-time remote support client updates with batched processing
 * Only use this when actively viewing the Remote Support page
 * @param {function} onClientUpdate - Callback when a client is updated
 * @param {function} onClientInsert - Callback when a new client is created
 * @param {function} onClientDelete - Callback when a client is deleted
 * @param {boolean} enabled - Whether the subscription is enabled
 */
export const useRealtimeRemoteSupport = (
  onClientUpdate,
  onClientInsert = null,
  onClientDelete = null,
  enabled = true
) => {
  // Refs for batching updates
  const pendingInserts = useRef(new Map());
  const pendingUpdates = useRef(new Map());
  const pendingDeletes = useRef(new Set());
  const batchTimeout = useRef(null);

  // Process all pending events in a single batch
  const processBatch = useCallback(async () => {
    const inserts = new Map(pendingInserts.current);
    const updates = new Map(pendingUpdates.current);
    const deletes = new Set(pendingDeletes.current);

    pendingInserts.current.clear();
    pendingUpdates.current.clear();
    pendingDeletes.current.clear();

    // Process deletes immediately (no fetch needed)
    if (deletes.size > 0 && onClientDelete) {
      deletes.forEach((id) => onClientDelete(id));
    }

    // Combine inserts and updates for a single batch fetch
    const allIds = [...inserts.keys(), ...updates.keys()];
    if (allIds.length === 0) return;

    try {
      const { data, error } = await supabase
        .from('remote_support_clients')
        .select('*')
        .in('id', allIds);

      if (error) {
        console.error('Realtime: Error fetching remote support batch:', error);
        return;
      }

      if (data) {
        data.forEach((client) => {
          if (inserts.has(client.id) && onClientInsert) {
            onClientInsert(client);
          } else if (updates.has(client.id) && onClientUpdate) {
            onClientUpdate(client);
          }
        });
      }
    } catch (error) {
      console.error('Realtime: Remote support batch fetch failed:', error);
    }
  }, [onClientUpdate, onClientInsert, onClientDelete]);

  // Queue an event for batched processing
  const queueEvent = useCallback(
    (payload) => {
      if (payload.eventType === 'INSERT' && payload.new && onClientInsert) {
        pendingInserts.current.set(payload.new.id, payload);
      } else if (payload.eventType === 'UPDATE' && payload.new && onClientUpdate) {
        pendingUpdates.current.set(payload.new.id, payload);
      } else if (payload.eventType === 'DELETE' && payload.old && onClientDelete) {
        pendingDeletes.current.add(payload.old.id);
      }

      // Reset the batch timer
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      batchTimeout.current = setTimeout(processBatch, BATCH_DELAY_MS);
    },
    [processBatch, onClientUpdate, onClientInsert, onClientDelete],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Subscribe to all changes on the remote_support_clients table
    const channel = supabase
      .channel('remote-support-clients-realtime')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'remote_support_clients'
        },
        queueEvent
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.error('Realtime: Failed to subscribe to remote support');
        }
      });

    // Cleanup subscription on unmount
    return () => {
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      supabase.removeChannel(channel);
    };
  }, [enabled, queueEvent]);
};
